import { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import Marquee from "react-fast-marquee";
import { ArrowRight, Brain, Waves, LineChart, Sparkles } from "lucide-react";
import api from "@/lib/api";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import HeroCanvas from "@/components/HeroCanvas";
import ProgramCard from "@/components/ProgramCard";
import { MaskedLines, Reveal } from "@/components/Reveal";

const MANIFESTO = [
  { n: "01", title: "The brain is trainable", body: "Neuroplasticity is not a promise — it is a mechanism. Every session reshapes the pathways that govern your attention." },
  { n: "02", title: "Feedback is the teacher", body: "You cannot improve what you cannot see. We render your brain's electrical rhythm into a signal you can steer in real time." },
  { n: "03", title: "Calm is a skill", body: "Focus is not force. It is the practiced ability to return — again and again — to a state of quiet, alert clarity." },
];

const SCIENCE = [
  { icon: Brain, label: "Neuroplasticity", value: "6 wks", note: "to measurable baseline shift" },
  { icon: Waves, label: "Alpha-theta training", value: "40Hz", note: "targeted gamma entrainment" },
  { icon: LineChart, label: "Tracked sessions", value: "1.2M+", note: "logged across the platform" },
  { icon: Sparkles, label: "Reported clarity", value: "94%", note: "of members after 30 days" },
];

export default function Landing() {
  const [programs, setPrograms] = useState([]);
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const opacity = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  useEffect(() => { api.get("/programs").then((r) => setPrograms(r.data)).catch(() => {}); }, []);

  return (
    <div className="relative bg-soma-bg min-h-screen">
      <Nav />

      {/* HERO */}
      <section ref={heroRef} className="relative min-h-screen flex items-center pt-28 pb-20">
        <div className="absolute inset-0 z-0">
          <div className="absolute right-0 top-0 w-full lg:w-[65%] h-full pointer-events-none">
            <HeroCanvas />
          </div>
          <div className="absolute inset-0 bg-gradient-to-r from-soma-bg via-soma-bg/60 to-transparent" />
        </div>

        <motion.div style={{ y, opacity }} className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 w-full">
          <div className="max-w-4xl">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 0.2 }}
              className="flex items-center gap-3 mb-8">
              <span className="w-8 h-px bg-soma-primary" />
              <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Neurofeedback training platform</span>
            </motion.div>

            <h1 className="font-display font-semibold text-white leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-[4.25rem]">
              <MaskedLines lines={["Train the", "signal beneath", "your focus."]} delay={0.3} lineClass="text-glow" />
            </h1>

            <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.1, duration: 0.8 }}
              className="mt-10 text-lg text-soma-muted max-w-xl leading-relaxed">
              neurofdback.ca turns your brain's electrical rhythm into a signal you can steer. Guided neurofeedback programs that rebuild attention, flow, and calm — measurably.
            </motion.p>

            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 1.3, duration: 0.8 }}
              className="mt-12 flex flex-wrap items-center gap-4">
              <Link to="/programs" data-testid="hero-cta-programs"
                className="group flex items-center gap-3 px-8 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300 cyan-glow">
                Explore programs
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
              </Link>
              <a href="#science" data-testid="hero-cta-science"
                className="px-8 py-4 rounded-full border border-white/15 text-white hover:border-soma-primary hover:text-soma-primary transition-colors duration-300">
                The science
              </a>
            </motion.div>
          </div>
        </motion.div>
      </section>

      {/* MARQUEE */}
      <section className="relative z-10 border-y border-soma-border py-8 bg-soma-surface/30">
        <Marquee speed={40} gradient={false} autoFill>
          {["Focus", "Flow", "Clarity", "Calm", "Attention", "Recovery", "Resilience", "Presence"].map((w, i) => (
            <span key={i} className="font-serif-editorial italic text-3xl lg:text-4xl text-soma-muted mx-10 flex items-center gap-10">
              {w} <span className="text-soma-primary not-italic text-2xl">✦</span>
            </span>
          ))}
        </Marquee>
      </section>

      {/* SCIENCE */}
      <section id="science" className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-end mb-20">
          <div className="lg:col-span-7">
            <Reveal>
              <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">The evidence</span>
              <h2 className="font-display font-semibold text-3xl lg:text-5xl mt-6 leading-[0.95]">
                Measurable change,<br /><span className="text-soma-muted">not vague promises.</span>
              </h2>
            </Reveal>
          </div>
          <div className="lg:col-span-4 lg:col-start-9">
            <Reveal delay={0.15}>
              <p className="text-soma-muted leading-relaxed">
                Every neurofdback.ca protocol is built on closed-loop EEG feedback. We track your progress the way a lab would — and hand you the controls.
              </p>
            </Reveal>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-soma-border border border-soma-border">
          {SCIENCE.map((s, i) => (
            <Reveal key={i} delay={i * 0.08} className="bg-soma-bg p-8 lg:p-10 group hover:bg-soma-surface transition-colors duration-500">
              <s.icon className="w-6 h-6 text-soma-primary mb-8" strokeWidth={1.5} />
              <div className="font-display font-semibold text-3xl lg:text-4xl text-white mb-2">{s.value}</div>
              <div className="text-sm text-white mb-1">{s.label}</div>
              <div className="text-xs text-soma-muted">{s.note}</div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* MANIFESTO */}
      <section id="manifesto" className="relative z-10 bg-soma-surface/40 border-y border-soma-border py-28 lg:py-40">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
          <Reveal className="mb-20 max-w-2xl">
            <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Manifesto</span>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl mt-6 leading-[0.95]">Three beliefs we build on.</h2>
          </Reveal>

          <div className="space-y-px">
            {MANIFESTO.map((m, i) => (
              <Reveal key={m.n} delay={i * 0.1}>
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 py-12 border-t border-soma-border group">
                  <div className="lg:col-span-2">
                    <span className="font-serif-editorial italic text-4xl text-soma-primary/60 group-hover:text-soma-primary transition-colors duration-500">{m.n}</span>
                  </div>
                  <div className="lg:col-span-5">
                    <h3 className="font-display text-xl lg:text-2xl font-bold text-white">{m.title}</h3>
                  </div>
                  <div className="lg:col-span-4 lg:col-start-9">
                    <p className="text-soma-muted leading-relaxed">{m.body}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* PROGRAMS PREVIEW */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 py-28 lg:py-40">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-6 mb-16">
          <Reveal>
            <span className="text-xs uppercase tracking-[0.35em] text-soma-primary">Programs</span>
            <h2 className="font-display font-semibold text-3xl lg:text-5xl mt-6 leading-[0.95]">Choose your protocol.</h2>
          </Reveal>
          <Reveal delay={0.15}>
            <Link to="/programs" data-testid="landing-view-all"
              className="group flex items-center gap-2 text-white hover:text-soma-primary transition-colors">
              View all <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
          </Reveal>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {programs.slice(0, 3).map((p, i) => <ProgramCard key={p.id} program={p} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="relative z-10 max-w-[1400px] mx-auto px-6 lg:px-10 pb-28">
        <Reveal>
          <div className="relative overflow-hidden border border-soma-border bg-soma-surface p-12 lg:p-24">
            <div className="absolute -right-20 -top-20 w-96 h-96 rounded-full bg-soma-primary/10 blur-3xl" />
            <div className="relative max-w-2xl">
              <h2 className="font-display font-semibold text-3xl lg:text-5xl leading-[0.95] mb-8">
                Your first session<br />starts today.
              </h2>
              <p className="text-soma-muted text-lg mb-10 max-w-lg">Join thousands training their attention with closed-loop neurofeedback. No hardware required to begin.</p>
              <Link to="/register" data-testid="cta-register"
                className="inline-flex items-center gap-3 px-8 py-4 rounded-full bg-soma-primary text-soma-bg font-semibold hover:bg-soma-primaryHover transition-colors duration-300 cyan-glow">
                Create your account <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        </Reveal>
      </section>

      <Footer />
    </div>
  );
}
